/** App-level pages: errors, 404, 403, "this account can't use this panel". */

import { isRouteErrorResponse, Link, useRouteError } from "react-router";

import { isApiError } from "@motolynk/api-client";
import { useTranslation } from "@motolynk/i18n";
import { AuthLayout, Button, EmptyState, ErrorState } from "@motolynk/ui";

import { useAppConfig } from "./config";
import { useLogout } from "./session";

export function NotFoundPage() {
  const { t } = useTranslation();
  const { homePath = "/" } = useAppConfig();
  return (
    <EmptyState
      icon="search"
      title={t("errors.notFoundTitle")}
      description={t("errors.notFoundBody")}
      action={
        <Button asChild variant="outline">
          <Link to={homePath}>{t("errors.goHome")}</Link>
        </Button>
      }
    />
  );
}

export function ForbiddenPage() {
  const { t } = useTranslation();
  return (
    <EmptyState
      icon="shield"
      title={t("errors.forbiddenTitle")}
      description={t("errors.forbiddenBody")}
    />
  );
}

/** Router error boundary: a crashed page never blanks the whole app. */
export function RouteErrorPage() {
  const error = useRouteError();
  const { t } = useTranslation();
  if (isRouteErrorResponse(error) && error.status === 404) return <NotFoundPage />;
  console.error(error);
  return (
    <ErrorState
      title={t("errors.crashTitle")}
      description={t("errors.crashBody")}
      reference={isApiError(error) ? error.requestId : null}
      referenceLabel={t("errors.reference")}
      retryLabel={t("actions.retry")}
      onRetry={() => window.location.reload()}
    />
  );
}

/** Logged in, but the account may not use this panel (e.g. a customer opening the admin panel). */
export function NoPanelAccessPage({ message }: { message: string }) {
  const { t } = useTranslation();
  const config = useAppConfig();
  const logout = useLogout();
  return (
    <AuthLayout
      product={config.product}
      title={t("noAccess.title")}
      description={message}
      headline={t(config.authCopy.headline)}
      subheadline={t(config.authCopy.subheadline)}
      points={config.authCopy.points.map((key) => t(key))}
    >
      <Button block variant="secondary" loading={logout.isPending} onClick={() => logout.mutate()}>
        {t("noAccess.switchAccount")}
      </Button>
    </AuthLayout>
  );
}

/** Shown by the vendor panel (and later the storefront) while `platform.maintenance_mode` is on. */
export function MaintenancePage() {
  const { t } = useTranslation();
  const config = useAppConfig();
  const logout = useLogout();
  return (
    <AuthLayout
      product={config.product}
      title={t("maintenance.title")}
      description={t("maintenance.body")}
      headline={t(config.authCopy.headline)}
      subheadline={t(config.authCopy.subheadline)}
      points={config.authCopy.points.map((key) => t(key))}
    >
      <div className="flex flex-col gap-3">
        <Button block onClick={() => window.location.reload()}>
          {t("maintenance.retry")}
        </Button>
        <Button
          block
          variant="secondary"
          loading={logout.isPending}
          onClick={() => logout.mutate()}
        >
          {t("maintenance.logout")}
        </Button>
      </div>
    </AuthLayout>
  );
}
